// src/services/sessionsApiService.js
// API service for DVA Admin Backend (sessions/client/user management)

const SESSIONS_API_URL = import.meta.env.VITE_SESSIONS_API_URL || 'https://dva-admin-backend-dev-813595575009.us-central1.run.app';
const PUBLIC_API_KEY = import.meta.env.VITE_PUBLIC_API_KEY;

/**
 * Generic fetch wrapper for sessions API
 */
const fetchWithErrorHandling = async (path, options = {}) => {
  const url = `${SESSIONS_API_URL}${path}`;

  const defaultOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': PUBLIC_API_KEY,
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);

    // Parse response body
    const data = await response.json();

    if (!response.ok) {
      // Include error details from response body if available
      const errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error(`Sessions API request failed: ${url}`, error);
    throw error;
  }
};

/**
 * Create a new client (organization)
 *
 * @param {Object} clientData - Client information
 * @param {string} clientData.client_name - Name of the organization
 * @param {string} clientData.client_type - Type of organization (e.g., "health_system")
 * @param {number} clientData.beds - Number of beds
 * @param {number} clientData.lives_under_vbc_agreements - Lives under value-based care agreements
 * @returns {Promise<Object>} Created client data with client_id
 */
export const createClient = async (clientData) => {
  try {
    const response = await fetchWithErrorHandling('/public/clients', {
      method: 'POST',
      body: JSON.stringify(clientData),
    });

    return response;
  } catch (error) {
    console.error('Failed to create client:', error);
    throw error;
  }
};

/**
 * Create a new admin user for a client
 *
 * @param {string|number} clientId - The client ID
 * @param {Object} userData - User information
 * @param {string} userData.name_first - User's first name
 * @param {string} userData.name_last - User's last name
 * @param {string} userData.email - User's email address
 * @param {string} userData.role - User's role (default: "CLIENT_ADMIN")
 * @returns {Promise<Object>} Created user data
 */
export const createClientAdmin = async (clientId, userData) => {
  try {
    const response = await fetchWithErrorHandling(`/public/clients/${clientId}/admin`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    return response;
  } catch (error) {
    console.error('Failed to create client admin:', error);
    throw error;
  }
};

/**
 * Complete registration flow: create client + create admin user
 *
 * @param {Object} registrationData
 * @param {Object} registrationData.client - Client data
 * @param {Object} registrationData.user - User data
 * @returns {Promise<Object>} Registration result with client and user data
 */
export const registerClientAndUser = async ({ client, user }) => {
  try {
    // Step 1: Create the client
    const clientResponse = await createClient(client);

    // Extract client_id from response (API returns 'id' field)
    const clientId = clientResponse.data?.id || clientResponse.id || clientResponse.client_id;

    if (!clientId) {
      console.error('Client response:', clientResponse);
      throw new Error('Client created but no client_id returned');
    }

    // Step 2: Create the admin user for this client
    const userResponse = await createClientAdmin(clientId, {
      ...user,
      role: user.role || 'CLIENT_ADMIN'
    });

    return {
      success: true,
      client: clientResponse,
      user: userResponse,
      clientId
    };
  } catch (error) {
    console.error('Registration flow failed:', error);
    throw error;
  }
};

/**
 * Get pricing options for subscription plans
 *
 * @param {string} authToken - Firebase auth token (Bearer token)
 * @returns {Promise<Object>} Pricing data with monthly/annual options
 */
export const getPricingOptions = async (authToken) => {
  try {
    const url = `${SESSIONS_API_URL}/admin/subscriptions/prices`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('Failed to fetch pricing options:', error);
    throw error;
  }
};

/**
 * Create a Stripe checkout session for subscription upgrade
 *
 * @param {string|number} clientId - The client ID
 * @param {Object} checkoutData - Checkout session parameters
 * @param {string} checkoutData.plan_id - Target plan ID (e.g., "TIER_2")
 * @param {string} checkoutData.billing_cycle - "MONTHLY" or "ANNUAL"
 * @param {string} checkoutData.success_url - URL to redirect after success
 * @param {string} checkoutData.cancel_url - URL to redirect on cancellation
 * @param {string} authToken - Firebase auth token (Bearer token)
 * @returns {Promise<Object>} Checkout session with checkout_url and session_id
 */
export const createCheckoutSession = async (clientId, checkoutData, authToken) => {
  try {
    const url = `${SESSIONS_API_URL}/admin/clients/${clientId}/subscription/checkout`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(checkoutData),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('Failed to create checkout session:', error);
    throw error;
  }
};

/**
 * Get the sessions API base URL (for debugging)
 */
export const getSessionsApiUrl = () => SESSIONS_API_URL;

const sessionsApiService = {
  createClient,
  createClientAdmin,
  registerClientAndUser,
  getSessionsApiUrl,
  getPricingOptions,
  createCheckoutSession,
};

export default sessionsApiService;
